import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
} from 'components/gyramais';
import UploadCard from 'components/cards/UploadCard';
import {
  Col,
  Select,
  Form,
  message,
  Checkbox,
} from 'antd';
import { formatAndNotificateError } from 'utils';
import { useMutation } from '@apollo/client';
import { SAVE_LOAN_EVENT } from 'components/drawers/RevenueStatementDrawer/graphql';
import { SIGNATURE_TYPE, SAVE_CCB_LOAN } from './graphql';

const CcbModal = ({
  visible,
  onClose,
  loanId,
  isBadesul,
  refetch,
  loading,
}) => {
  const { Item } = Form;
  const { Option } = Select;

  const [accepted, setAccepted] = useState(false);
  const [signatureTypeSelect, setSignatureTypeSelect] = useState();
  const [ccbFileSignature, setCcbFileSignature] = useState();
  const [modalConfirm, setModalConfirm] = useState(false);
  const [uploadKey, setUploadKey] = useState(Date.now());

  const [signatureType] = useMutation(SIGNATURE_TYPE, {
    onCompleted: () => { },
    onError: (error) => formatAndNotificateError(error),
  });

  const [saveLoanEvent] = useMutation(SAVE_LOAN_EVENT, {
    onCompleted: () => {
      message.success('Empréstimo salvo com sucesso!');
      refetch();
      setModalConfirm(false);
      onClose();
    },
    onError: (error) => formatAndNotificateError(error),
  });

  const [saveCcbLoan] = useMutation(SAVE_CCB_LOAN, {
    onCompleted: async () => {
      await saveLoanEvent({
        variables: {
          loanId,
          statusValue: 'signed',
        },
      });
    },
    onError: (error) => {
      formatAndNotificateError(error);
      setModalConfirm(false);
    },
  });

  useEffect(() => {
    setUploadKey(Date.now());
  }, [signatureTypeSelect]);

  // O modal fica montado no ClientCard, entao sem resetar aqui um fechamento sem
  // salvar deixa o arquivo e o aceite anteriores vivos: ao reabrir, o "Salvar" ja
  // viria habilitado com o ccbFileId descartado. Cada abertura comeca limpa.
  // Operações Badesul só aceitam assinatura GOV (validada via BRy com as
  // credenciais do Badesul); pré-seleciona e não oferece física/cartório. (GYR-1489)
  useEffect(() => {
    if (!visible) return;

    setAccepted(false);
    setCcbFileSignature(undefined);
    setModalConfirm(false);
    setSignatureTypeSelect(isBadesul ? 'gov' : undefined);
    setUploadKey(Date.now());
  }, [visible, isBadesul]);

  return (
    <>
      <Modal
        style={{ whiteSpace: 'pre-line' }}
        title="Adicionar documento CCB"
        centered
        visible={visible}
        onCancel={onClose}
        okButton={{
          visible: true,
          text: 'Salvar',
          onClick: () => setModalConfirm(true),
          disabled: !(accepted && signatureTypeSelect && ccbFileSignature),
        }}
      >
        <Col xs={24} sm={24} md={24}>
          {/* Os Item aqui servem só de espaçamento: este modal não tem <Form>, e com
              `name` o rc-field-form sobrescrevia o value do filho com undefined,
              impedindo o Select de exibir o tipo escolhido. (GYR-1599) */}
          <Item>
            <Select
              id="signatureType"
              className="gyramais-input"
              placeholder="Escolha o tipo da assinatura"
              value={signatureTypeSelect}
              onChange={(e) => {
                setSignatureTypeSelect(e);
                setCcbFileSignature(false);
                setUploadKey(Date.now());
              }}
            >
              {!isBadesul && (
                <Option value="PhysicsWithNotarization">Física com reconhecimento em cartório</Option>
              )}
              {!isBadesul && (
                <Option value="physicalSignature">Somente física</Option>
              )}
              <Option value="gov">GOV</Option>
            </Select>
          </Item>
        </Col>

        <Col xs={24} sm={24} md={24}>
          <Item>
            <UploadCard
              key={uploadKey}
              id="uploadCcbDocumentId"
              accept="application/pdf, .pdf"
              loading={loading}
              label="Documento CCB"
              validateSignature={signatureTypeSelect === 'gov'}
              onCompleted={async ({ id: fileId }) => {
                setCcbFileSignature(fileId);
              }}
              loanId={loanId}
            />
          </Item>
        </Col>

        <Item>
          <Checkbox
            checked={accepted}
            id="ccb-document-terms"
            onChange={({ target: { checked } }) => setAccepted(checked)}
          >
            <p>
              <b>
                Confirmo que o PDF está assinado por todas as partes do contrato
              </b>
            </p>
          </Checkbox>
        </Item>
      </Modal>

      {modalConfirm && (
        <Modal
          style={{ width: '100px' }}
          title="Confirma o envio da CCB assinada?"
          centered
          visible={modalConfirm}
          okButton={{
            visible: true,
            text: 'Confirmar',
            onClick: async () => {
              // o onError do Apollo trata a rejeicao e a promise resolve, entao sem
              // checar `errors` o loan iria para signed com o tipo nao persistido
              const { errors } = await signatureType({
                variables: {
                  loanId,
                  typeValue: signatureTypeSelect,
                },
              });

              if (errors) {
                setModalConfirm(false);
                return;
              }

              await saveCcbLoan({
                variables: {
                  id: loanId,
                  ccbFileId: ccbFileSignature,
                },
              });
            },
          }}
          onClose={() => setModalConfirm(false)}
          cancelButton={{
            visible: true,
            text: 'Cancelar',
            color: 'danger',
            onClick: () => setModalConfirm(false),
          }}
        >
          <></>
        </Modal>
      )}
    </>
  );
};

CcbModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  loanId: PropTypes.string,
  isBadesul: PropTypes.bool,
  refetch: PropTypes.func,
  loading: PropTypes.bool,
};

export default memo(CcbModal);
